export const M5_UNIT_12 = {
  id: "m5-u12-short-questions",
  title: "Unit 12: Making Conversation",
  topic: "Short questions to show interest and keep conversations flowing naturally",
  iconType: "comments",
  grammarImage: "/grammar/grammar-m5-u12.jpg",
  grammarText: "This unit teaches you short questions—a natural conversation technique used by native speakers to show interest and keep dialogue flowing. When someone says 'I went to Paris,' you respond with 'Did you?' (not 'Really?' or silence). Short questions use auxiliary verbs (do, does, did, is, are, was, were, will, can) to echo the statement. They make you sound engaged and interested. This is real conversational English—not textbook questions, but the way people actually talk. You'll learn UK standard forms (inverted) and US casual variants (non-inverted), making your English sound authentic and natural in any English-speaking country.",

  sets: [
    // ===================================================================
    // SET 1: DISCOVERING SHORT QUESTIONS (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u12-s1",
      title: "Set 1: What Are Short Questions?",
      questions: [
        {
          type: "match",
          q: "Match statements to natural responses:",
          pairs: [
            { left: "I went to the beach yesterday", right: "Did you? (shows interest)" },
            { left: "It was really hot", right: "Was it? (engaged response)" },
            { left: "I love pizza", right: "Do you? (natural reaction)" }
          ],
          a: "completed",
          successtip: "✓ Short questions = conversational glue! Shows you're listening actively"
        },
        {
          type: "mc",
          q: "CONVERSATION: 'I went to the movies last night.' Best response to show interest:",
          options: ["Did you?", "Really?", "OK."],
          a: "Did you?",
          tip: "Short question = natural, engaged, native-like",
          successtip: "✓ 'Did you?' > 'Really?'. Sounds more native and keeps conversation flowing!"
        },
        {
          type: "input",
          q: "PATTERN: 'I visited Paris.' → '___ you?' (past auxiliary)",
          a: "Did",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Use auxiliary that matches the tense: visited (past) → Did"
        },
        {
          type: "order",
          q: "Build short question response:",
          words: ["I", "love", "coffee.", "→", "Do", "you?"],
          a: "I love coffee. → Do you?",
          successtip: "✓ Statement (I love) → Short question (Do you?)"
        },
        {
          type: "mc",
          q: "STATEMENT: 'It was amazing!' Short question response:",
          options: ["Was it?", "It was?", "Really was?"],
          a: "Was it?",
          tip: "With 'be', invert: It was → Was it?",
          successtip: "✓ UK/Standard: 'Was it?' (inverted). US casual: 'It was?' (non-inverted) also OK"
        },
        {
          type: "input",
          q: "RESPONSE: 'She's a doctor.' → '___ she?' (present be)",
          a: "Is",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "Match statement types to auxiliaries:",
          pairs: [
            { left: "I play tennis (present)", right: "Do you?" },
            { left: "I played tennis (past)", right: "Did you?" },
            { left: "I am tired (be)", right: "Are you?" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "WHY use short questions? They:",
          options: ["Show interest and keep conversation flowing", "Are grammatically required", "Replace full questions"],
          a: "Show interest and keep conversation flowing",
          tip: "Short questions = engagement tool, not grammar rule",
          successtip: "✓ Native speakers use these constantly! Makes you sound interested and fluent"
        },
        {
          type: "input",
          q: "CONVERSATION: 'We're going to Italy.' → 'Are ___?' (subject pronoun)",
          a: "you",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Invert subject: We are → Are you? (or Are we?)"
        },
        {
          type: "order",
          q: "Natural dialogue order:",
          words: ["They", "won", "the", "game.", "Did", "they?", "That's", "great!"],
          a: "They won the game. Did they? That's great!",
          successtip: "✓ Short question bridges statement → reaction. Natural flow!"
        }
      ]
    },

    // ===================================================================
    // SET 2: SHORT QUESTIONS WITH "BE" (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u12-s2",
      title: "Set 2: Was It? Is It? Are They?",
      questions: [
        {
          type: "match",
          q: "Match 'be' statements to short questions:",
          pairs: [
            { left: "It was cold", right: "Was it?" },
            { left: "She is here", right: "Is she?" },
            { left: "They were late", right: "Were they?" }
          ],
          a: "completed",
          successtip: "✓ With 'be', just INVERT: It was → Was it?"
        },
        {
          type: "mc",
          q: "WEATHER: 'It was really hot yesterday.' Response:",
          options: ["Was it?", "Did it?", "Is it?"],
          a: "Was it?",
          tip: "Past tense (was) → Was it?"
        },
        {
          type: "input",
          q: "PRESENT: 'The movie is good.' → '___ it?' (present be)",
          a: "Is",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Build conversation:",
          words: ["She", "was", "at", "the", "party.", "Was", "she?"],
          a: "She was at the party. Was she?"
        },
        {
          type: "mc",
          q: "PLURAL: 'They were amazing performers.' Response:",
          options: ["Were they?", "Was they?", "Did they?"],
          a: "Were they?",
          tip: "They were → Were they? (plural)"
        },
        {
          type: "input",
          q: "NEGATIVE: 'He wasn't there.' → '___ he?' (negative short question)",
          a: "Wasn't",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Can use negative: wasn't, weren't, isn't, aren't",
          successtip: "✓ Negative short questions show surprise: 'Wasn't he?' = Really? He wasn't?"
        },
        {
          type: "match",
          q: "Match tenses with 'be':",
          pairs: [
            { left: "It is hot → Is it?", right: "Present" },
            { left: "It was hot → Was it?", right: "Past" },
            { left: "It will be hot → Will it?", right: "Future" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "OPINION: 'The concert was incredible!' Natural response:",
          options: ["Was it?", "Really?", "Both work"],
          a: "Both work",
          tip: "'Was it?' is more native/engaged. 'Really?' also OK but less conversational",
          successtip: "✓ 'Was it?' sounds more native than 'Really?' Try using it more!"
        },
        {
          type: "input",
          q: "LOCATION: 'They're in London now.' → 'Are ___?' (subject)",
          a: "they",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Traduce diálogo: Estaba delicioso. ¿Sí? Me alegro.",
          words: ["It", "was", "delicious.", "Was", "it?", "I'm", "glad."],
          a: "It was delicious. Was it? I'm glad.",
          successtip: "✓ Perfect conversational flow with short question!"
        }
      ]
    },

    // ===================================================================
    // SET 3: SHORT QUESTIONS WITH "DO/DOES" (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u12-s3",
      title: "Set 3: Do You? Does He?",
      questions: [
        {
          type: "match",
          q: "Match present simple statements to short questions:",
          pairs: [
            { left: "I play tennis", right: "Do you?" },
            { left: "She likes coffee", right: "Does she?" },
            { left: "They work here", right: "Do they?" }
          ],
          a: "completed",
          successtip: "✓ Present simple → Do/Does (not the main verb!)"
        },
        {
          type: "mc",
          q: "HOBBY: 'I play guitar every day.' Response:",
          options: ["Do you?", "Play you?", "Are you?"],
          a: "Do you?",
          tip: "Present simple (play) → Do you?",
          successtip: "✓ Don't repeat the verb! 'Do you?' (not 'Do you play?')"
        },
        {
          type: "input",
          q: "THIRD PERSON: 'He speaks French.' → '___ he?' (auxiliary for he/she/it)",
          a: "Does",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "He/she/it → Does (not Do!)"
        },
        {
          type: "order",
          q: "Build natural response:",
          words: ["I", "love", "that", "restaurant.", "Do", "you?"],
          a: "I love that restaurant. Do you?"
        },
        {
          type: "mc",
          q: "ROUTINE: 'She goes to the gym every morning.' Response:",
          options: ["Does she?", "Do she?", "Goes she?"],
          a: "Does she?",
          tip: "Third person singular → Does"
        },
        {
          type: "input",
          q: "PLURAL: 'They work downtown.' → 'Do ___?' (subject)",
          a: "they",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "Match subjects to auxiliaries (present):",
          pairs: [
            { left: "I/You/We/They", right: "Do" },
            { left: "He/She/It", right: "Does" },
            { left: "Always short!", right: "Just auxiliary + subject" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "NEGATIVE: 'I don't like seafood.' Response:",
          options: ["Don't you?", "Do you?", "Both work"],
          a: "Both work",
          tip: "Negative: 'Don't you?' (surprise). Positive: 'Do you?' (interest)",
          successtip: "✓ 'Don't you?' = surprise (Really? You don't?). 'Do you?' = neutral interest"
        },
        {
          type: "input",
          q: "HABIT: 'We eat out every Friday.' → '___ you?' (present auxiliary)",
          a: "Do",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Conversation: Ella cocina muy bien. ¿Sí? Deberías probar su comida.",
          words: ["She", "cooks", "really", "well.", "Does", "she?", "You", "should", "try", "her", "food."],
          a: "She cooks really well. Does she? You should try her food.",
          successtip: "✓ 'Does she?' keeps conversation flowing to next statement!"
        }
      ]
    },

    // ===================================================================
    // SET 4: SHORT QUESTIONS WITH "DID" (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u12-s4",
      title: "Set 4: Did You? Did She?",
      questions: [
        {
          type: "match",
          q: "Match past statements to short questions:",
          pairs: [
            { left: "I went to Paris", right: "Did you?" },
            { left: "She called me", right: "Did she?" },
            { left: "They won the game", right: "Did they?" }
          ],
          a: "completed",
          successtip: "✓ Past simple → DID (all subjects use 'did'!)"
        },
        {
          type: "mc",
          q: "TRAVEL: 'I visited Japan last year.' Response:",
          options: ["Did you?", "Were you?", "Visited you?"],
          a: "Did you?",
          tip: "Past simple → Did",
          successtip: "✓ ALL subjects use 'did' in past: Did I? Did you? Did he/she/it/we/they?"
        },
        {
          type: "input",
          q: "EVENT: 'They had a great time.' → '___ they?' (past auxiliary)",
          a: "Did",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Natural conversation:",
          words: ["I", "saw", "that", "movie.", "Did", "you?", "How", "was", "it?"],
          a: "I saw that movie. Did you? How was it?"
        },
        {
          type: "mc",
          q: "NEWS: 'She got the job!' Response:",
          options: ["Did she?", "Was she?", "Has she?"],
          a: "Did she?",
          tip: "Got (past simple) → Did she?"
        },
        {
          type: "input",
          q: "EXPERIENCE: 'We ate at that new restaurant.' → 'Did ___?' (subject)",
          a: "you",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "Match past contexts to responses:",
          pairs: [
            { left: "I finished the project", right: "Did you? That's great!" },
            { left: "He didn't come", right: "Didn't he? That's strange!" },
            { left: "They loved it", right: "Did they? I'm glad!" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "NEGATIVE: 'I didn't see you at the party.' Response:",
          options: ["Didn't you?", "Did you?", "Both work"],
          a: "Both work",
          tip: "Didn't you? (surprise). Did you? (interest/clarification)",
          successtip: "✓ Negative echoes statement. Positive shows surprise at the negative"
        },
        {
          type: "input",
          q: "SURPRISE: 'He failed the test.' → '___ he? I thought he studied!' (past negative)",
          a: "Did",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Didn't"] }
        },
        {
          type: "order",
          q: "Traduce: Fui al concierto. ¿Sí? ¿Cómo estuvo?",
          words: ["I", "went", "to", "the", "concert.", "Did", "you?", "How", "was", "it?"],
          a: "I went to the concert. Did you? How was it?",
          successtip: "✓ Short question → follow-up question. Natural conversation chain!"
        }
      ]
    },

    // ===================================================================
    // SET 5: MIXED TENSES PRACTICE (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u12-s5",
      title: "Set 5: Mixed Tenses",
      questions: [
        {
          type: "match",
          q: "Match statements to correct short questions:",
          pairs: [
            { left: "I'm going tomorrow (be)", right: "Are you?" },
            { left: "I go every week (do)", right: "Do you?" },
            { left: "I went yesterday (did)", right: "Did you?" }
          ],
          a: "completed",
          successtip: "✓ Identify the verb type: be/do/did/will/can, then invert!"
        },
        {
          type: "mc",
          q: "PRESENT CONTINUOUS: 'I'm learning Spanish.' Response:",
          options: ["Are you?", "Do you?", "Did you?"],
          a: "Are you?",
          tip: "'Am learning' = present continuous with 'be' → Are you?"
        },
        {
          type: "input",
          q: "FUTURE: 'She's leaving tomorrow.' → '___ she?' (future with be)",
          a: "Is",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Build conversation with present perfect:",
          words: ["I've", "been", "to", "Paris.", "Have", "you?"],
          a: "I've been to Paris. Have you?",
          successtip: "✓ Present perfect (have/has) → Have you? / Has he?"
        },
        {
          type: "mc",
          q: "ABILITY: 'I can speak three languages.' Response:",
          options: ["Can you?", "Do you?", "Are you?"],
          a: "Can you?",
          tip: "Modal 'can' → Can you?",
          successtip: "✓ Modals: can/could/will/would/should/might → just invert!"
        },
        {
          type: "input",
          q: "FUTURE WILL: 'He'll be here soon.' → '___ he?' (future auxiliary)",
          a: "Will",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "Match verb forms to auxiliaries:",
          pairs: [
            { left: "Present continuous (am/is/are + -ing)", right: "Are you?" },
            { left: "Present perfect (have/has + past participle)", right: "Have you?" },
            { left: "Future will", right: "Will you?" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "PAST CONTINUOUS: 'I was sleeping.' Response:",
          options: ["Were you?", "Did you?", "Are you?"],
          a: "Were you?",
          tip: "Was sleeping (past continuous) → Were you?"
        },
        {
          type: "input",
          q: "OBLIGATION: 'I have to work tonight.' → 'Do ___?' (subject)",
          a: "you",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Have to' = verb expression → Do you?",
          successtip: "✓ 'Have to' (obligation) uses 'do': Do you? But 'have' (possession) uses 'Have you?'"
        },
        {
          type: "order",
          q: "Complex conversation:",
          words: ["I've", "never", "tried", "sushi.", "Haven't", "you?", "You", "should!"],
          a: "I've never tried sushi. Haven't you? You should!",
          successtip: "✓ Negative present perfect → Haven't you? (surprise + encouragement)"
        }
      ]
    },

    // ===================================================================
    // SET 6: NEGATIVE SHORT QUESTIONS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u12-s6",
      title: "Set 6: Didn't You? Wasn't It?",
      questions: [
        {
          type: "match",
          q: "Negative short questions express:",
          pairs: [
            { left: "Surprise", right: "Didn't you? (Really?)" },
            { left: "Confirmation", right: "Wasn't it? (Checking)" },
            { left: "Concern", right: "Aren't you? (Worried tone)" }
          ],
          a: "completed",
          successtip: "✓ Negative short questions = stronger emotion (surprise, concern, checking)"
        },
        {
          type: "mc",
          q: "SURPRISE: 'I didn't go to the party.' Response showing surprise:",
          options: ["Didn't you?", "Did you?", "Both work"],
          a: "Didn't you?",
          tip: "Negative response = surprise/confirmation",
          successtip: "✓ 'Didn't you?' = But I thought you would! (surprise). 'Did you?' = neutral"
        },
        {
          type: "input",
          q: "CHECKING: 'It wasn't expensive.' → '___ it? I thought it would be.' (negative be)",
          a: "Wasn't",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Build surprised response:",
          words: ["You", "don't", "like", "chocolate?", "Don't", "you?", "That's", "unusual!"],
          a: "You don't like chocolate? Don't you? That's unusual!"
        },
        {
          type: "mc",
          q: "CONCERN: 'He isn't coming tonight.' Response showing concern:",
          options: ["Isn't he?", "Is he?", "Both same meaning"],
          a: "Isn't he?",
          tip: "'Isn't he?' emphasizes surprise/concern more",
          successtip: "✓ Negative = stronger emotion. 'Isn't he?' > 'Is he?' in impact"
        },
        {
          type: "input",
          q: "PAST NEGATIVE: 'They weren't at the meeting.' → '___ they? Where were they?' (past be negative)",
          a: "Weren't",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "Match negative short questions to contexts:",
          pairs: [
            { left: "Didn't you know?", right: "Surprise at lack of knowledge" },
            { left: "Wasn't it great?", right: "Confirming positive experience" },
            { left: "Can't you come?", right: "Disappointed they can't attend" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "CONFIRMATION: 'The movie was terrible.' Response agreeing:",
          options: ["Wasn't it?", "Was it?", "Both mean same"],
          a: "Wasn't it?",
          tip: "'Wasn't it?' = I agree! 'Was it?' = neutral/questioning",
          successtip: "✓ Negative confirms/agrees: 'Wasn't it terrible?' = Yes, I agree!"
        },
        {
          type: "input",
          q: "ABILITY: 'He can't swim.' → '___ he? That's surprising for someone who grew up by the beach.' (negative modal)",
          a: "Can't",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Traduce con sorpresa: No fuiste a la fiesta. ¿No? ¿Por qué no?",
          words: ["You", "didn't", "go", "to", "the", "party.", "Didn't", "you?", "Why", "not?"],
          a: "You didn't go to the party. Didn't you? Why not?",
          successtip: "✓ 'Didn't you?' + 'Why not?' = surprise + seeking reason"
        }
      ]
    },

    // ===================================================================
    // SET 7: US VS UK VARIANTS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u12-s7",
      title: "Set 7: UK vs US English",
      questions: [
        {
          type: "match",
          q: "Compare UK and US short questions:",
          pairs: [
            { left: "UK Standard: Did you?", right: "Inverted (formal)" },
            { left: "US Casual: You did?", right: "Non-inverted (informal)" },
            { left: "Both correct!", right: "Different registers" }
          ],
          a: "completed",
          successtip: "✓ UK: inverted (Did you?). US casual: non-inverted (You did?). Know both!"
        },
        {
          type: "mc",
          q: "STATEMENT: 'I went to the concert.' UK STANDARD response:",
          options: ["Did you?", "You did?", "Both acceptable"],
          a: "Did you?",
          tip: "UK/Standard/Formal = inverted",
          successtip: "✓ UK/International English: 'Did you?' (inverted). More formal/standard"
        },
        {
          type: "input",
          q: "US CASUAL variant: 'I went to the concert.' → '___ did?' (non-inverted)",
          a: "You",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "US casual = subject + auxiliary (no inversion)",
          successtip: "✓ US casual: 'You did?' Common in American conversation, slightly informal"
        },
        {
          type: "order",
          q: "Build UK standard:",
          words: ["I", "love", "this", "song.", "Do", "you?"],
          a: "I love this song. Do you?",
          successtip: "✓ UK/Standard: Do you? (auxiliary first)"
        },
        {
          type: "order",
          q: "Build US casual variant:",
          words: ["I", "love", "this", "song.", "You", "do?"],
          a: "I love this song. You do?",
          successtip: "✓ US casual: You do? (subject first). Conversational American"
        },
        {
          type: "mc",
          q: "WHICH IS MORE FORMAL?",
          options: ["Was it? (UK standard)", "It was? (US casual)", "Same formality"],
          a: "Was it? (UK standard)",
          tip: "Inverted = more formal/standard",
          successtip: "✓ Inverted (Was it?) = formal/standard. Non-inverted (It was?) = casual/US"
        },
        {
          type: "input",
          q: "NEGATIVE UK: 'They didn't go.' → '___ they?' (UK standard negative)",
          a: "Didn't",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ UK negative: Didn't they? US casual: They didn't? Both correct!"
        },
        {
          type: "match",
          q: "Match variants to contexts:",
          pairs: [
            { left: "Business meeting", right: "Use UK standard (Did you?)" },
            { left: "Casual chat with friends", right: "Either works (Did you? / You did?)" },
            { left: "International English", right: "UK standard safer (Did you?)" }
          ],
          a: "completed",
          successtip: "✓ When in doubt, use UK standard (inverted). Understood everywhere!"
        },
        {
          type: "mc",
          q: "US CASUAL: 'He's leaving tomorrow.' Casual response:",
          options: ["He is?", "Is he?", "Both work"],
          a: "Both work",
          tip: "Both acceptable. 'He is?' = casual US. 'Is he?' = standard",
          successtip: "✓ In US: 'He is?' common. In UK: 'Is he?' standard. Learn both!"
        },
        {
          type: "order",
          q: "VERSATILITY: Respond with BOTH variants:",
          words: ["I", "finished", "early.", "Did", "you?", "/", "You", "did?"],
          a: "I finished early. Did you? / You did?",
          successtip: "✓ PERFECT! Knowing both makes you versatile in any English-speaking country! 🇬🇧🇺🇸"
        }
      ]
    },

    // ===================================================================
    // SET 8: REAL CONVERSATIONS (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u12-s8",
      title: "Set 8: Natural Dialogues",
      questions: [
        {
          type: "match",
          q: "Short questions in conversation flow:",
          pairs: [
            { left: "Shows interest", right: "Keeps speaker talking" },
            { left: "Buys time", right: "While you think of response" },
            { left: "Sounds native", right: "Natural conversation rhythm" }
          ],
          a: "completed",
          successtip: "✓ Short questions = conversational super-tool! Multi-purpose"
        },
        {
          type: "mc",
          q: "CONVERSATION FLOW: 'I just got back from Mexico.' Best natural response:",
          options: ["Did you? How was it?", "That's nice.", "OK."],
          a: "Did you? How was it?",
          tip: "Short question + follow-up = perfect conversation flow",
          successtip: "✓ 'Did you?' shows interest, then follow-up deepens conversation. Native pattern!"
        },
        {
          type: "input",
          q: "BUILD FLOW: 'I'm thinking of changing jobs.' → '___ you? Why?' (short Q + follow-up)",
          a: "Are",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Natural multi-turn conversation:",
          words: ["I", "saw", "John", "yesterday.", "Did", "you?", "How", "is", "he?", "He's", "good!", "Is", "he?", "That's", "great!"],
          a: "I saw John yesterday. Did you? How is he? He's good! Is he? That's great!",
          successtip: "✓ Short questions appear TWICE! Natural conversation rhythm"
        },
        {
          type: "mc",
          q: "RESTAURANT CONTEXT: 'This pasta is delicious!' Friend responds:",
          options: ["Is it? Let me try!", "I know.", "Yes."],
          a: "Is it? Let me try!",
          tip: "'Is it?' shows engagement and leads to action",
          successtip: "✓ Short question + action = perfect conversational move!"
        },
        {
          type: "input",
          q: "MOVIE CHAT: 'I hated that movie.' → '___ you? I loved it!' (expressing opposite view)",
          a: "Did",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Short question acknowledges their view before stating yours",
          successtip: "✓ Short question softens disagreement: 'Did you? I loved it!' (polite disagreement)"
        },
        {
          type: "match",
          q: "Match contexts to short question functions:",
          pairs: [
            { left: "News sharing", right: "'Did you?' (interest)" },
            { left: "Opinion stated", right: "'Was it?' (engagement)" },
            { left: "Surprise fact", right: "'Is it?' (checking)" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "GOSSIP: 'Sarah got engaged!' Response:",
          options: ["Did she? When?", "Really?", "That's good."],
          a: "Did she? When?",
          tip: "'Did she?' more native than 'Really?'",
          successtip: "✓ 'Did she?' = native gossip response! More natural than 'Really?'"
        },
        {
          type: "input",
          q: "TRAVEL: 'I've never been to Asia.' → 'Haven't ___? You should go!' (present perfect negative)",
          a: "you",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Complete café conversation:",
          words: ["This", "coffee", "is", "amazing.", "Is", "it?", "I'll", "order", "one", "too."],
          a: "This coffee is amazing. Is it? I'll order one too.",
          successtip: "✓ Short question creates natural pause before decision/action!"
        }
      ]
    },

    // ===================================================================
    // SET 9: ADVANCED CONTEXTS (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u12-s9",
      title: "Set 9: Professional & Social Contexts",
      questions: [
        {
          type: "match",
          q: "Short questions in different settings:",
          pairs: [
            { left: "Work", right: "She got promoted. Did she?" },
            { left: "Social", right: "I'm moving to Berlin. Are you?" },
            { left: "Family", right: "Dad's retiring. Is he?" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "WORK: 'The meeting was cancelled.' Professional response:",
          options: ["Was it? Do you know why?", "Really?", "OK."],
          a: "Was it? Do you know why?",
          tip: "Short question + professional follow-up",
          successtip: "✓ Professional contexts: short questions sound engaged and competent!"
        },
        {
          type: "input",
          q: "JOB NEWS: 'I'm starting a new job next week.' → '___ you? Congratulations!' (future be)",
          a: "Are",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Social invitation flow:",
          words: ["We're", "having", "a", "party", "Saturday.", "Are", "you?", "Can", "I", "come?"],
          a: "We're having a party Saturday. Are you? Can I come?",
          successtip: "✓ Short question acknowledges before requesting invitation. Polite!"
        },
        {
          type: "mc",
          q: "BAD NEWS: 'I lost my wallet.' Sympathetic response:",
          options: ["Did you? That's terrible!", "Really?", "Oh no."],
          a: "Did you? That's terrible!",
          tip: "Short question shows you're listening actively",
          successtip: "✓ Short questions work for good AND bad news! Shows active listening"
        },
        {
          type: "input",
          q: "HEALTH: 'I'm not feeling well.' → 'Aren't ___? You should rest.' (concern)",
          a: "you",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Negative short question shows concern"
        },
        {
          type: "match",
          q: "Match tone to short question type:",
          pairs: [
            { left: "Positive news", right: "Did you? (excited)" },
            { left: "Negative news", right: "Did you? (sympathetic)" },
            { left: "Surprise", right: "Did you? (shocked)" }
          ],
          a: "completed",
          successtip: "✓ Same words, different TONES! Context + intonation = meaning"
        },
        {
          type: "mc",
          q: "FAMILY: 'My sister's having a baby!' Response:",
          options: ["Is she? That's wonderful!", "Really?", "Great!"],
          a: "Is she? That's wonderful!",
          tip: "Short question + enthusiasm = perfect reaction"
        },
        {
          type: "input",
          q: "ADVICE: 'I'm thinking of quitting my job.' → 'Are ___? Have you thought it through?' (caution)",
          a: "you",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Short question + concern question = caring response"
        },
        {
          type: "order",
          q: "Weather small talk:",
          words: ["It's", "supposed", "to", "rain", "tomorrow.", "Is", "it?", "I'll", "bring", "an", "umbrella."],
          a: "It's supposed to rain tomorrow. Is it? I'll bring an umbrella.",
          successtip: "✓ Short questions perfect for small talk! Keeps conversation light and flowing"
        }
      ]
    },

    // ===================================================================
    // SET 10: MASTERY - REAL-LIFE APPLICATION (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u12-s10",
      title: "Set 10: Conversation Mastery",
      questions: [
        {
          type: "match",
          q: "Short question superpowers:",
          pairs: [
            { left: "Sound native", right: "Natural conversation rhythm" },
            { left: "Show interest", right: "Keeps speaker engaged" },
            { left: "Buy thinking time", right: "Pause before full response" }
          ],
          a: "completed",
          successtip: "✓ Short questions = secret weapon of native speakers! Use them!"
        },
        {
          type: "mc",
          q: "CHALLENGE: 'I ran a marathon last weekend.' Multiple response options. Which is MOST native?",
          options: ["Did you? That's amazing! How was it?", "Really? That's cool.", "Wow."],
          a: "Did you? That's amazing! How was it?",
          tip: "Short Q + reaction + follow-up = native speaker pattern",
          successtip: "✓ Triple combo: Did you? (interest) + That's amazing! (reaction) + How was it? (engagement)"
        },
        {
          type: "input",
          q: "SPONTANEOUS: 'I just bought a car!' → '___ you? What kind?' (immediate reaction)",
          a: "Did",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Complex conversation with multiple short questions:",
          words: ["I'm", "learning", "guitar.", "Are", "you?", "That's", "cool!", "Can", "you", "play", "anything", "yet?", "A", "few", "chords.", "Can", "you?", "Keep", "practicing!"],
          a: "I'm learning guitar. Are you? That's cool! Can you play anything yet? A few chords. Can you? Keep practicing!",
          successtip: "✓ TWO short questions! Natural back-and-forth conversation flow! 🎸"
        },
        {
          type: "mc",
          q: "DISAGREEMENT: 'I don't think that's a good idea.' Polite disagreement:",
          options: ["Don't you? I think it could work.", "You're wrong.", "I disagree."],
          a: "Don't you? I think it could work.",
          tip: "Short question softens disagreement",
          successtip: "✓ 'Don't you?' acknowledges their view before presenting yours. Diplomatic!"
        },
        {
          type: "input",
          q: "EXCITEMENT: 'We won the competition!' → '___ you? Congratulations!' (past victory)",
          a: "Did",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "When NOT to use short questions:",
          pairs: [
            { left: "They're very upset", right: "Full sympathy better" },
            { left: "Formal presentation", right: "Too casual" },
            { left: "Written English", right: "Mainly spoken feature" }
          ],
          a: "completed",
          successtip: "✓ Short questions = SPOKEN feature. Less common in writing (except dialogue/texting)"
        },
        {
          type: "mc",
          q: "TEXTING: Are short questions used in text messages?",
          options: ["Yes, very common!", "No, too informal", "Only with close friends"],
          a: "Yes, very common!",
          tip: "Short questions perfect for texting! Quick and natural",
          successtip: "✓ Texting: 'I got the job!' 'Did you? 🎉' Very common in messages!"
        },
        {
          type: "input",
          q: "PRACTICE: Create short question: 'I can't believe it's already December!' → '___ it? Time flies!' (surprise agreement)",
          a: "Is",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Isn't"] }
        },
        {
          type: "order",
          q: "FINAL MASTERY: Build perfect conversational response:",
          words: ["She", "speaks", "five", "languages.", "Does", "she?", "That's", "incredible!", "I", "wish", "I", "could", "do", "that."],
          a: "She speaks five languages. Does she? That's incredible! I wish I could do that.",
          successtip: "✓ PERFECT! Short question + amazement + personal reaction. You're now a conversation master! 🎉🗣️"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - Café Conversation (10 questions)
  // ===================================================================
  // ===================================================================
// READING SET: A Café Conversation
// ===================================================================
readingSet: {
  id: "m5-u12-reading",
  title: "Reading: A Café Conversation",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Encounter & New Habits (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Tuesday Encounter**

Emma walked into her favorite café and saw her friend Lisa sitting by the window. "Hey Lisa! I didn't expect to see you here," Emma said. 

Lisa looked up and smiled. "Didn't you? I come here every Tuesday morning now. I discovered their new matcha latte last month." 

"Did you?" Emma responded. "Is it good?" 

"It's amazing! You should try it," Lisa said. Emma sat down and ordered one. "So, I heard you went to Barcelona last week," Emma said. "I did! It was incredible," Lisa replied.`
    },
    {
      type: "mc",
      q: "How often does Lisa visit the café now?",
      options: ["Every Tuesday morning", "Every day", "Once a month"],
      a: "Every Tuesday morning",
      successtip: "✓ ROUTINE! She comes every Tuesday. ☕"
    },
    {
      type: "input",
      q: "What new drink did Lisa discover at the café?",
      a: "matcha latte",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["new matcha latte", "matcha"] },
      successtip: "✓ MATCHA LATTE! A trendy choice! 🍵"
    },
    {
      type: "mc",
      q: "Which short question does Emma use to show interest in the latte discovery?",
      options: ["Did you?", "Really?", "Wow"],
      a: "Did you?",
      successtip: "✓ 'Did you?' = natural response! It matches the past tense 'discovered'. 🗣️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Barcelona Memories (preguntas 4-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Talking about Spain**

"Was it?" Emma asked, taking a sip of her matcha latte. "This is really good!" 

"Is it? I told you!" Lisa said with a smile. "So tell me about Barcelona. What did you do there?" Emma asked. 

"Well, I visited the Sagrada Familia, walked around the Gothic Quarter, and ate the best tapas of my life," Lisa explained. 

"Did you?" Emma said, leaning forward with interest. "Which tapas place did you go to?" 

"A little place called Cal Pep. You have to go if you ever visit!" Lisa said enthusiastically.`
    },
    {
      type: "mc",
      q: "What famous architectural landmark did Lisa visit?",
      options: ["Sagrada Familia", "Gothic Cathedral", "Casa Batlló"],
      a: "Sagrada Familia",
      successtip: "✓ SAGRADA FAMILIA! The most famous church in Barcelona! ⛪"
    },
    {
      type: "input",
      q: "What is the name of the restaurant Lisa recommends?",
      a: "Cal Pep",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ CAL PEP! Famous for its tapas! 🥘"
    },
    {
      type: "mc",
      q: "How many 'short questions' (Was it? Is it? Did you?) appear in this specific paragraph?",
      options: ["Three", "Two", "Four"],
      a: "Three",
      successtip: "✓ THREE! Natural conversation is full of these reactions! 📈"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Future Plans (preguntas 7-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Emma's Turn**

"I'm actually planning a trip to Spain next month," Emma said. 

"Are you?" Lisa replied. "That's great! Where are you going?" 

"I'm thinking Madrid and Seville," Emma answered. 

"Aren't you going to Barcelona?" Lisa asked, surprised. 

"I wasn't planning to, but now I'm considering it after hearing about your trip!" Emma laughed. "You should! And you'll love the architecture there," Lisa said. 

"Will I?" Emma smiled. "I'm not usually into architecture, but you've convinced me!"`
    },
    {
      type: "mc",
      q: "Where is Emma planning to go in Spain?",
      options: ["Madrid and Seville", "Only Barcelona", "Only Madrid"],
      a: "Madrid and Seville",
      successtip: "✓ MADRID & SEVILLE! Two beautiful cities! 🇪🇸"
    },
    {
      type: "input",
      q: "Which negative short question does Lisa use to show surprise?",
      a: "Aren't you",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Aren't you going to Barcelona"] },
      successtip: "✓ 'Aren't you?' = Negative short question for surprise! 😮"
    },
    {
      type: "mc",
      q: "What is Emma's usual attitude toward architecture?",
      options: ["Not usually interested", "Very passionate", "She's an architect"],
      a: "Not usually interested",
      successtip: "✓ She's not usually into it, but Lisa changed her mind! 🏗️"
    },
    {
      type: "mc",
      q: "COMPREHENSION: What is the main characteristic of this conversation?",
      options: ["Natural use of short questions to show interest", "Formal business English", "Grammar rules only"],
      a: "Natural use of short questions to show interest",
      successtip: "✓ PERFECT! You identified: Did you? Was it? Is it? Are you? Aren't you? Will I? All used naturally! 🗣️✨"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u12-review",
    title: "Unit 12 Review: Conversation Mastery",
    questions: [
      {
        type: "match",
        q: "Review: Match statements to short questions:",
        pairs: [
          { left: "I went to Paris", right: "Did you?" },
          { left: "It was amazing", right: "Was it?" },
          { left: "I love it", right: "Do you?" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "WITH 'BE': 'She's leaving tomorrow.' Short question:",
        options: ["Is she?", "Does she?", "Did she?"],
        a: "Is she?"
      },
      {
        type: "input",
        q: "PRESENT SIMPLE: 'He works downtown.' → '___ he?' (auxiliary)",
        a: "Does",
        accept: { ignoreCase: true, ignorePunctuation: true }
      },
      {
        type: "mc",
        q: "PAST SIMPLE: 'They won the game.' Short question:",
        options: ["Did they?", "Were they?", "Do they?"],
        a: "Did they?"
      },
      {
        type: "input",
        q: "NEGATIVE: 'I don't like it.' → '___ you?' (negative short Q)",
        a: "Don't",
        accept: { ignoreCase: true, ignorePunctuation: true }
      },
      {
        type: "mc",
        q: "UK VS US: Which is more formal/standard?",
        options: ["Did you? (UK/inverted)", "You did? (US/non-inverted)", "Same formality"],
        a: "Did you? (UK/inverted)"
      },
      {
        type: "order",
        q: "Build perfect conversational response:",
        words: ["I", "just", "got", "promoted!", "Did", "you?", "Congratulations!"],
        a: "I just got promoted! Did you? Congratulations!"
      },
      {
        type: "mc",
        q: "MODAL: 'I can speak French.' Short question:",
        options: ["Can you?", "Do you?", "Are you?"],
        a: "Can you?"
      },
      {
        type: "input",
        q: "PRESENT PERFECT: 'I've been there.' → 'Have ___?' (subject)",
        a: "you",
        accept: { ignoreCase: true, ignorePunctuation: true }
      },
      {
        type: "mc",
        q: "FINAL MASTERY: What's the PRIMARY purpose of short questions?",
        options: ["Show interest and keep conversation flowing naturally", "Replace full questions", "Grammar requirement"],
        a: "Show interest and keep conversation flowing naturally",
        successtip: "✓ CONGRATULATIONS! You've mastered conversational English! Short questions make you sound native, engaged, and natural. Use them constantly: Did you? Was it? Is it? Are you? Will you? Can you? Now you sound like a REAL English speaker! 🎉🗣️🇬🇧🇺🇸"
      }
    ]
  }
};
